import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { authApi } from '../services/api'
import toast from 'react-hot-toast'
import LoadingSpinner from '../components/LoadingSpinner'
import { Church } from '../types'

const forgotPasswordSchema = z.object({
  email: z.string().email('Invalid email address'),
  churchId: z.number().optional()
})

type ForgotPasswordForm = z.infer<typeof forgotPasswordSchema>

const ForgotPasswordPage = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [emailSent, setEmailSent] = useState(false)
  const [churches, setChurches] = useState<Church[]>([])
  const [loadingChurches, setLoadingChurches] = useState(true)

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<ForgotPasswordForm>({
    resolver: zodResolver(forgotPasswordSchema)
  })

  // Fetch churches on component mount
  useEffect(() => {
    const fetchChurches = async () => {
      try {
        const response = await fetch('/api/churches?status=approved')
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        const churches = await response.json()
        // Ensure churches is an array
        setChurches(Array.isArray(churches) ? churches : [])
      } catch (error) {
        console.error('Failed to fetch churches:', error)
        setChurches([]) // Set empty array on error
      } finally {
        setLoadingChurches(false)
      }
    }

    fetchChurches()
  }, [])

  const onSubmit = async (data: ForgotPasswordForm) => {
    setIsLoading(true)
    try {
      const requestData = {
        email: data.email,
        ...(data.churchId && { churchId: data.churchId })
      }
      await authApi.forgotPassword(requestData.email, requestData.churchId)
      setEmailSent(true)
      toast.success('Password reset code sent to your email')
    } catch (error: any) {
      console.error('Forgot password failed:', error)
      toast.error(error.response?.data?.error || 'Failed to send reset email')
    } finally {
      setIsLoading(false)
    }
  }

  if (emailSent) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Check your email
          </h2>
          <div className="mt-8 bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                We've sent a 6-digit verification code to your email address.
                Please check your inbox and use the code to reset your password.
              </p>
              <div className="space-y-2">
                <Link
                  to="/reset-password"
                  className="block w-full text-center bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 text-sm font-medium"
                >
                  Enter Verification Code
                </Link>
                <Link
                  to="/login"
                  className="block text-center text-blue-600 hover:text-blue-500 text-sm font-medium"
                >
                  Back to Sign In
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Reset your password
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Enter your email address and church, and we'll send you a verification code to reset your password.
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <input
                {...register('email')}
                type="email"
                autoComplete="email"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your email address"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="churchId" className="block text-sm font-medium text-gray-700">
                Your Church (Optional)
              </label>
              {loadingChurches ? (
                <div className="mt-1 p-3 text-center text-gray-500">
                  Loading churches...
                </div>
              ) : (
                <select
                  {...register('churchId', { valueAsNumber: true })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select your church (optional)</option>
                  {churches.map((church) => (
                    <option key={church.id} value={church.id}>
                      {church.name} {church.location && `- ${church.location}`}
                    </option>
                  ))}
                </select>
              )}
              {errors.churchId && (
                <p className="mt-1 text-sm text-red-600">{errors.churchId.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Selecting your church helps verify your identity for security purposes
              </p>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  'Send verification code'
                )}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="text-center">
              <Link
                to="/login"
                className="text-sm text-blue-600 hover:text-blue-500"
              >
                Back to Sign In
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ForgotPasswordPage